import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { signupUser } from "../redux/auth/authSlice";
import { getEventLocations } from "../redux/eventLocation/eventLocationSlice";
import { getRoles } from "../redux/role/roleSlice";
import { toast } from "react-toastify";
import ReactCrop from "react-image-crop";
import { getCompanyCategories } from "../redux/companyCategory/companyCategorySlice";
import "react-image-crop/dist/ReactCrop.css";
import { sanitizeValue } from "../helper/helper";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { countryCodes } from "../helper/countryCodes";
import { getUserCountry } from "../helper/getUserCountry";
import { countryOptions, customStyles, formatOptionLabel } from "./CountryCode/helper";

const AddUserLayer = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { locations = [] } = useSelector((state) => state.eventLocation);
  const { roles = [] } = useSelector((state) => state.role);
  const { categories = [] } = useSelector((state) => state.companyCategory);

  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({
    unit: "%",
    width: 50,
    aspect: 1
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [errors, setErrors] = useState({
    email: "",
    phone: "",
  });
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const imgRef = React.useRef(null);

  const [formData, setFormData] = useState({
    roleId: "",
    locationId: "",
    email: "",
    countryCode: "+91",
    phone: "",
    fullName: "",
    country: "",
    city: "",
    fcmToken: "",
    designation: "",
    company: "",
    companyCategoryId: "",
    allowed: 1,
  });

  const selectedRole = roles.find((r) => r.id === formData.roleId);
  const isSponsor = selectedRole?.name === "SPONSOR";
  useEffect(() => {
    dispatch(getEventLocations());
    dispatch(getRoles());
  }, [dispatch]);

  useEffect(() => {
    const detectCountry = async () => {
      const data = await getUserCountry();

      if (data) {
        const matched = countryCodes.find(
          (c) => c.iso2 === data.iso2
        );

        if (matched) {
          setFormData((prev) => ({
            ...prev,
            countryCode: matched.dialCode,
            country: matched.name
          }));
        }
      }
    };

    detectCountry();
  }, []);


  const handleBlur = (e) => {
    const { name, value } = e.target;

    const sanitized = sanitizeValue(name, value);

    setFormData((prev) => ({
      ...prev,
      [name]: sanitized,
    }));
  };
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };
  const handleChange = (e) => {
    let { name, value } = e.target;

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    if (name === "phone") {
      value = value.replace(/\D/g, "");

      setErrors((prev) => ({
        ...prev,
        phone:
          value.length === 0
            ? "Phone is required"
            : value.length < 6
              ? "Phone too short"
              : value.length > 15
                ? "Phone too long"
                : "",
      }));
    }

    if (name === "email") {
      setErrors((prev) => ({
        ...prev,
        email:
          value.length === 0
            ? "Email is required"
            : !emailRegex.test(value)
              ? "Enter a valid email"
              : "",
      }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (isSponsor) {
      dispatch(getCompanyCategories());
    }
  }, [isSponsor, dispatch]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      setCropSrc(event.target.result);
      setShowCropModal(true);
    };

    reader.readAsDataURL(file);
  };
  const handleCropSave = async () => {

    if (!completedCrop || !imgRef.current) return;

    const canvas = document.createElement("canvas");
    const image = imgRef.current;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    const ctx = canvas.getContext("2d");

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg")
    );

    const file = new File([blob], "profile.jpg", { type: "image/jpeg" });

    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(blob));

    setShowCropModal(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!emailRegex.test(formData.email)) {
      toast.error("Enter a valid email");
      return;
    }

    if (!/^\d{6,15}$/.test(formData.phone)) {
      toast.error("Enter valid phone number");
      return;
    }

    if (!formData.company) {
      toast.error("Company Name is required");
      return;
    }
    // if (!formData.company || !formData.companyCategoryId) {
    //   toast.error("Company Name & Category are required for Sponsor");
    //   return;
    // }
    if (isSponsor) {
      if (isSponsor && !logoFile) {
        toast.error("Company Logo is required");
        return;
      }

      if (formData.allowed < 0) {
        toast.error("Allowed must be 0 or greater");
        return;
      }

      if (formData.allowed > 99) {
        toast.error("Allowed cannot exceed 99");
        return;
      }
    }

    const sanitizedData = {};

    Object.keys(formData).forEach((key) => {
      sanitizedData[key] = sanitizeValue(key, formData[key]);
    });

    const data = new FormData();

    data.append("roleId", formData.roleId);
    data.append("locationId", formData.locationId);
    data.append("fcmToken", formData.fcmToken);

    data.append("email", sanitizedData.email);
    data.append("phone", sanitizedData.phone);
    data.append("countryCode", formData.countryCode);
    data.append("fullName", sanitizedData.fullName);
    data.append("designation", sanitizedData.designation);
    data.append("country", sanitizedData.country);
    data.append("city", sanitizedData.city);
    data.append("company", formData.company);

    if (isSponsor) {
      data.append("companyCategoryId", formData.companyCategoryId);
      data.append("allowed", formData.allowed);
    }

    if (imageFile instanceof File) {
      data.append("profile", imageFile);
    }
    if (isSponsor && logoFile instanceof File) {
      data.append("logo", logoFile);
    }
    dispatch(signupUser(data))
      .unwrap()
      .then((res) => {

        toast.success(res?.message || "User created successfully");

        setFormData({
          roleId: "",
          locationId: "",
          email: "",
          countryCode: "+91",
          phone: "",
          fullName: "",
          country: "",
          city: "",
          fcmToken: "",
          designation: "",
          company: "",
          companyCategoryId: "",
          allowed: 1

        });

        setImagePreviewUrl("");
        setImageFile(null);
        setLogoFile(null);
        setLogoPreview(null);
        setTimeout(() => {
          navigate("/users-list");
        }, 800)

      })
      .catch((err) => {
        console.log(err, "FULL ERROR");

        const res = err?.response?.data || err;

        if (res?.errors && Array.isArray(res.errors)) {
          const newErrors = {};

          res.errors.forEach((e) => {
            newErrors[e.field] = e.message;

            toast.error(e.message);
          });

          setErrors((prev) => ({
            ...prev,
            ...newErrors,
          }));

          return;
        }

        const message =
          res?.message ||
          err?.message ||
          "Something went wrong";

        toast.error(message);
      });
  };

  return (
    <div className="card h-100 p-0 radius-12">

      <div className="card-body p-12">

        <div className="row justify-content-center">

          <div className="col-xxl-6 col-xl-8 col-lg-10">

            <div className="card border">

              <div className="card-body">

                <h6 className="text-md text-primary-light mb-16">
                  Profile Image
                </h6>

                {/* Image Upload */}

                <div className="mb-24 mt-16">

                  <div className="avatar-upload">

                    <div className="avatar-edit position-absolute bottom-0 end-0 me-24 mt-16 z-1 cursor-pointer">

                      <input
                        type="file"
                        id="imageUpload"
                        accept=".png, .jpg, .jpeg"
                        hidden
                        onChange={handleImageChange}
                      />

                      <label
                        htmlFor="imageUpload"
                        className="w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 bg-hover-primary-100 text-lg rounded-circle"
                      >
                        <Icon icon="solar:camera-outline" />
                      </label>

                    </div>

                    <div className="avatar-preview">

                      <div
                        id="imagePreview"
                        style={{
                          backgroundImage: imagePreviewUrl
                            ? `url(${imagePreviewUrl})`
                            : `url(/assets/images/user-grid-img13.png)`
                        }}
                      />

                    </div>

                  </div>

                </div>

                {/* Form */}

                <form onSubmit={handleSubmit}>

                  <div className="mb-20">

                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Full Name <span className="text-danger-600">*</span>
                    </label>

                    <input
                      type="text"
                      className="form-control radius-8"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter full name"
                      onBlur={handleBlur}
                    />

                  </div>

                  <div className="mb-20">

                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Email <span className="text-danger-600">*</span>
                    </label>

                    <input
                      type="email"
                      className="form-control radius-8"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter email"
                    />

                    {errors.email && (
                      <small className="text-danger">{errors.email}</small>
                    )}

                  </div>

                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Phone <span className="text-danger-600">*</span>
                    </label>

                    <div style={{ display: "flex", gap: "10px" }}>

                      <div style={{ width: "200px" }}>
                        <Select
                          options={countryOptions}
                          value={countryOptions.find(c => c.value === formData.countryCode)}
                          onChange={(selected) => {
                            const code = selected?.value.replace(/\D/g, "").slice(0, 4);

                            setFormData((prev) => ({
                              ...prev,
                              countryCode: "+" + code
                            }));
                          }}
                          isSearchable
                          styles={customStyles}
                          formatOptionLabel={formatOptionLabel}
                          menuPlacement="auto"
                        />
                      </div>

                      {/* Phone Input */}
                      <input
                        type="text"
                        className="form-control radius-8"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Enter phone"
                        maxLength={15}
                        inputMode="numeric"
                      />
                    </div>

                    {errors.phone && (
                      <small className="text-danger">{errors.phone}</small>
                    )}
                  </div>

                  {/* Role */}

                  <div className="mb-20">

                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Role <span className="text-danger-600">*</span>
                    </label>

                    <select
                      className="form-control radius-8 form-select"
                      name="roleId"
                      value={formData.roleId}
                      onChange={handleChange}
                    >

                      <option value="">Select Role</option>

                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}

                    </select>

                  </div>

                  {/* Event Location */}

                  <div className="mb-20">

                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Event Location <span className="text-danger-600">*</span>
                    </label>

                    <select
                      className="form-control radius-8 form-select"
                      name="locationId"
                      value={formData.locationId}
                      onChange={handleChange}
                    >

                      <option value="">Select Location</option>

                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}

                    </select>

                  </div>
                  {/* COMPANY NAME */}
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Company Name <span className="text-danger">*</span>
                    </label>

                    <input
                      type="text"
                      className="form-control radius-8"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Enter company name"
                    />
                  </div>

                  {isSponsor && (
                    <>
                      {/* LOGO UPLOAD */}
                      <div className="mb-20">
                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                          Company Logo <span className="text-danger">*</span>
                        </label>

                        <label
                          className="upload-file border radius-8 border-dashed d-flex align-items-center justify-content-center"
                          style={{ height: "150px", cursor: "pointer" }}
                        >
                          {logoPreview ? (
                            <img
                              src={logoPreview}
                              className="w-100 h-100 object-fit-cover radius-8"
                              alt="logo"
                            />
                          ) : (
                            <div className="text-center">
                              <Icon icon="solar:camera-outline" width="24" />
                              <p className="mb-0">
                                Upload Logo{" "}
                                <small className="fst-italic">(400x400)</small>
                              </p>
                            </div>
                          )}

                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleLogoChange}
                          />
                        </label>
                      </div>

                      {/* CATEGORY */}
                      <div className="mb-20">
                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                          Category <span className="text-danger">*</span>
                        </label>

                        <select
                          className="form-control radius-8 form-select"
                          name="companyCategoryId"
                          value={formData.companyCategoryId}
                          onChange={handleChange}
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* ALLOWED */}
                      <div className="mb-20">
                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                          Allowed Representatives
                        </label>

                        <input
                          type="number"
                          className="form-control radius-8"
                          name="allowed"
                          value={formData.allowed}
                          min="0"
                          max="99"
                          onChange={(e) => {
                            let value = Number(e.target.value);

                            if (value > 99) {
                              toast.error("Max allowed is 99");
                              value = 99;
                            }

                            if (value < 0) value = 0;

                            setFormData({
                              ...formData,
                              allowed: value,
                            });
                          }}
                        />
                      </div>
                    </>
                  )}

                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Designation
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      placeholder="Enter designation"
                    />
                  </div>

                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      City
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Enter city"
                    />
                  </div>

                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Country
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder="Enter country"
                    />
                  </div>

                  {/* Buttons */}

                  <div className="d-flex align-items-center justify-content-center gap-3">

                    <button
                      type="submit"
                      className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                    >
                      Save
                    </button>

                  </div>

                </form>

              </div>

            </div>

          </div>

        </div>

      </div>
      {showCropModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            background: "rgba(0,0,0,0.5)",
            zIndex: 9999
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">

              <div className="modal-header">
                <h5>Crop Image</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowCropModal(false)}
                />
              </div>

              <div className="modal-body text-center">

                {cropSrc && (
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={1}
                  >
                    <img
                      ref={imgRef}
                      src={cropSrc}
                      alt="crop"
                      style={{ maxHeight: "400px" }}
                    />
                  </ReactCrop>
                )}

              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={handleCropSave}
                >
                  Save Image
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddUserLayer;